import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Btn } from "./Primitives";
import { onAnchor } from "../../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];
const LINES = ["Sicher schwimmen", "lernen.", "Mit Vertrauen", "wachsen."];

const Line = ({ text, delay, italic }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span
        className={`block ${italic ? "font-light italic text-sky-200" : ""}`}
        initial={reduce ? false : { y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
      >
        {text}
      </motion.span>
    </span>
  );
};

const Fade = ({ children, delay, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div className={className} initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay }}>
      {children}
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "14%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, reduce ? 1.04 : 1.16]);

  return (
    <section id="start" ref={ref} data-testid="hero-section" className="relative isolate min-h-[100svh] overflow-hidden bg-navy text-white">
      <div className="absolute inset-0 overflow-hidden lg:left-[46%]">
        <motion.div
          className="absolute inset-0"
          style={{ y, scale }}
          initial={reduce ? false : { clipPath: "inset(12% 8% 12% 8%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.1 }}
        >
          <picture>
            <source media="(max-width: 767px)" srcSet="/images/hero-sm.webp" />
            <img
              src="/images/hero.webp"
              alt="Kind mit Schwimmbrille taucht unter Wasser durch ein klares Becken"
              fetchPriority="high"
              className="h-full w-full object-cover object-[50%_45%]"
            />
          </picture>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/45 lg:bg-gradient-to-r lg:from-navy lg:via-navy/10 lg:to-transparent" />
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-navy/90 via-navy/50 to-transparent" />

      <div className="relative mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 pb-10 pt-32 sm:px-8 lg:justify-center lg:px-12 lg:pb-16">
        <Fade delay={0.2}>
          <p className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-sky-200">
            <span className="h-px w-8 bg-sky-200/70" /> <span className="hidden sm:inline">Schwimmschule ·</span> Sportbad Dieburg
          </p>
        </Fade>
        <h1 data-testid="hero-headline" className="text-[2.6rem] font-extrabold leading-[1.02] tracking-[-0.03em] sm:text-6xl lg:text-[4.6rem] xl:text-[5.2rem]">
          <span className="sr-only">Sicher schwimmen lernen. Mit Vertrauen wachsen.</span>
          <span aria-hidden="true">
            {LINES.map((t, i) => (
              <Line key={t} text={t} delay={0.35 + i * 0.12} italic={i >= 2} />
            ))}
          </span>
        </h1>
        <Fade delay={1.0} className="mt-7 max-w-md">
          <p className="text-base leading-relaxed text-white/85 sm:text-lg">
            Mit Freude, Geduld und individueller Betreuung begleiten wir Kinder auf ihrem Weg zu mehr Sicherheit und Selbstvertrauen im Wasser.
          </p>
        </Fade>
        <Fade delay={1.15} className="mt-9 flex flex-col gap-3 sm:flex-row">
          <Btn href="#kurse" onClick={onAnchor("kurse")} testid="hero-kids-btn">
            Kinderkurse <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Btn>
          <Btn href="#erwachsene" onClick={onAnchor("erwachsene")} variant="ghost" testid="hero-adults-btn">
            Erwachsenenkurse <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Btn>
        </Fade>
        <Fade delay={1.35} className="mt-12 hidden items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/70 lg:flex">
          <ArrowDown className="h-4 w-4 animate-bounce" /> Scrollen
        </Fade>
      </div>
    </section>
  );
};
